import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { isImageAsset, resolveThumbKind } from './assetUtils';
import { buildTitleFromFilename } from '../../utils/titleFormatting';
import { useUploadQueue } from '../../context/UploadQueueContext';

const THUMB_CACHE_LIMIT = 150;
const thumbCache = new Map();

const getAssetTimestamp = (asset) => {
  if (!asset) return 0;
  const raw =
    asset.updated_at ||
    asset.updatedAt ||
    asset.modified_at ||
    asset.modifiedAt ||
    asset.created_at ||
    asset.createdAt ||
    asset.timestamp ||
    asset.created;
  const parsed = typeof raw === 'number' ? raw : Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getAssetSortKey = (asset) => {
  const timestamp = getAssetTimestamp(asset);
  if (timestamp) return timestamp;
  const idNum = Number.parseInt(asset?.id, 10);
  return Number.isFinite(idNum) ? idNum : 0;
};

const getCacheKey = (asset, thumbKind) => {
  if (!asset || !thumbKind) return '';
  const stamp = getAssetTimestamp(asset);
  return `${asset.id || 'unknown'}:${thumbKind}:${stamp}`;
};

const setThumbCache = (key, url) => {
  if (!key || !url) return;
  if (thumbCache.has(key)) {
    thumbCache.delete(key);
  }
  thumbCache.set(key, { url, ts: Date.now() });
  if (thumbCache.size > THUMB_CACHE_LIMIT) {
    const oldestKey = thumbCache.keys().next().value;
    const entry = thumbCache.get(oldestKey);
    if (entry?.url) URL.revokeObjectURL(entry.url);
    thumbCache.delete(oldestKey);
  }
};

function PhotoThumbnail({ asset, isSelected, selectionIndex, onSelect, rootRef, priority }) {
  const [thumbSrc, setThumbSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(Boolean(priority));
  const targetRef = useRef(null);
  const thumbKind = useMemo(() => resolveThumbKind(asset), [asset]);
  const directThumbUrl = useMemo(
    () => asset?.thumbnail_api_url || asset?.preview_api_url || asset?.thumbnail_url || null,
    [asset?.thumbnail_api_url, asset?.preview_api_url, asset?.thumbnail_url]
  );
  const cacheKey = useMemo(() => getCacheKey(asset, thumbKind), [asset?.id, thumbKind, asset?.updated_at, asset?.updatedAt, asset?.modified_at, asset?.modifiedAt, asset?.created_at, asset?.createdAt, asset?.timestamp, asset?.created]);

  useEffect(() => {
    if (priority) {
      setShouldLoad(true);
    }
  }, [priority]);

  useEffect(() => {
    if (shouldLoad || priority) return;
    if (!targetRef.current) return;
    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { root: rootRef?.current || null, rootMargin: '200px' }
    );
    observer.observe(targetRef.current);
    return () => observer.disconnect();
  }, [rootRef, priority, shouldLoad]);

  useEffect(() => {
    if (!shouldLoad || !asset || (!thumbKind && !directThumbUrl)) {
      setThumbSrc(null);
      return () => {};
    }

    let active = true;
    const controller = new AbortController();
    const token = localStorage.getItem('token');

    async function loadThumb() {
      if (cacheKey && thumbCache.has(cacheKey)) {
        setThumbSrc(thumbCache.get(cacheKey).url);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const requestUrl = directThumbUrl || `/api/creative/assets/${asset.id}/files/${encodeURIComponent(thumbKind)}`;
        const res = await fetch(requestUrl, {
          cache: 'force-cache',
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to load thumbnail (${res.status})`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        if (cacheKey) {
          setThumbCache(cacheKey, objectUrl);
        }
        if (active) setThumbSrc(objectUrl);
      } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to fetch asset thumbnail', err);
      if (active) setThumbSrc(null);
    } finally {
      if (active) setLoading(false);
    }
  }

    loadThumb();

    return () => {
      active = false;
      controller.abort();
    };
  }, [asset?.id, thumbKind, cacheKey, shouldLoad]);

  return (
    <button
      ref={targetRef}
      type='button'
      onClick={() => onSelect(asset)}
      className={`relative w-full h-32 rounded-md overflow-hidden border ${
        isSelected ? 'border-indigo-500 ring-2 ring-indigo-500' : 'border-gray-700'
      }`}
    >
      {thumbSrc ? (
        <img src={thumbSrc} alt='Asset thumbnail' className='w-full h-full object-cover' />
      ) : (
        <div className='w-full h-full bg-gray-800 flex items-center justify-center text-2xl'>🖼️</div>
      )}
      {Number.isFinite(selectionIndex) && selectionIndex > 0 && (
        <div className='absolute top-1 left-1 bg-indigo-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full'>
          {selectionIndex}
        </div>
      )}
      {loading && (
        <div className='absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-xs uppercase tracking-wide'>
          Loading...
        </div>
      )}
    </button>
  );
}

const normalizeFolder = (value) => (value || '').replace(/^\/+|\/+$/g, '');

const getFolderEmoji = (name) => {
  const lower = String(name || '').toLowerCase();
  if (lower.includes('upload')) return '⬆️';
  if (lower.includes('photo')) return '📷';
  if (lower.includes('logo')) return '🧩';
  if (lower.includes('ai')) return '✨';
  if (lower.includes('announce')) return '📣';
  return '📁';
};

const PhotoAssetBrowser = React.forwardRef(function PhotoAssetBrowser({
  assets,
  siteId,
  promptText,
  campaignTitle,
  selectedAssetId,
  selectedAssetIds,
  multiSelect = false,
  onSelectAsset,
  onToggleAsset,
  onUploadComplete,
  className,
  useFolders = false,
  leadingAction = null,
}, ref) {
  const [generating, setGenerating] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [editUseSelected, setEditUseSelected] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState('');
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [search, setSearch] = useState('');
  const [remoteAssets, setRemoteAssets] = useState([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const fileInputRef = useRef(null);
  const listRef = useRef(null);
  const completedBatchIdsRef = useRef(new Set());
  const { items, enqueueFiles } = useUploadQueue();

  const loadFolders = useCallback(async () => {
    if (!siteId || !useFolders) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/assets?siteId=${encodeURIComponent(siteId)}&all=1&includeFolders=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load folders');
      setFolders(data.folders || []);
    } catch (err) {
      setRemoteError(err.message || 'Failed to load folders');
    }
  }, [siteId, useFolders]);

  const loadAssets = useCallback(async () => {
    if (!siteId || !useFolders) return;
    setRemoteLoading(true);
    setRemoteError('');
    try {
      const token = localStorage.getItem('token');
      const folderParam = normalizeFolder(currentFolder);
      const url = `/api/assets?siteId=${encodeURIComponent(siteId)}&folder=${encodeURIComponent(folderParam)}&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load assets');
      setRemoteAssets(Array.isArray(data.assets) ? data.assets : []);
    } catch (err) {
      setRemoteError(err.message || 'Failed to load assets');
    } finally {
      setRemoteLoading(false);
    }
  }, [siteId, useFolders, currentFolder, search]);

  useEffect(() => {
    if (!useFolders) return;
    loadFolders();
  }, [useFolders, loadFolders]);

  useEffect(() => {
    if (!useFolders) return;
    loadAssets();
  }, [useFolders, loadAssets]);

  const folderList = useMemo(() => {
    const normalized = folders.map(normalizeFolder).filter(Boolean);
    const unique = Array.from(new Set(normalized));
    return unique.sort((a, b) => a.localeCompare(b));
  }, [folders]);

  const photoAssets = useMemo(
    () => {
      const source = useFolders ? remoteAssets : assets;
      const filtered = Array.isArray(source) ? source.filter((asset) => isImageAsset(asset)) : [];
      return filtered.slice().sort((a, b) => getAssetSortKey(b) - getAssetSortKey(a));
    },
    [assets, remoteAssets, useFolders]
  );

  const batchItems = useMemo(
    () => (activeBatchId ? items.filter((item) => String(item.batchId || '') === String(activeBatchId)) : []),
    [activeBatchId, items]
  );

  const uploadBatch = useMemo(() => {
    if (!batchItems.length) {
      return {
        totalCount: 0,
        settledCount: 0,
        completedCount: 0,
        failedCount: 0,
        isBusy: false,
        currentItem: null,
        progressPct: 0,
      };
    }

    const settledStatuses = new Set(['done', 'error', 'canceled']);
    const activeStatuses = new Set(['presigning', 'uploading', 'finalizing']);
    const settledCount = batchItems.filter((item) => settledStatuses.has(item.status)).length;
    const completedCount = batchItems.filter((item) => item.status === 'done').length;
    const failedCount = batchItems.filter((item) => item.status === 'error' || item.status === 'canceled').length;
    const currentItem = batchItems.find((item) => activeStatuses.has(item.status))
      || batchItems.find((item) => item.status === 'queued')
      || null;
    const progressUnits = batchItems.reduce((sum, item) => {
      if (item.status === 'done' || item.status === 'error' || item.status === 'canceled') return sum + 1;
      if (item.status === 'finalizing') return sum + 0.92;
      if (item.status === 'uploading') {
        const ratio = item.total ? Math.min(1, (item.loaded || 0) / item.total) : 0;
        return sum + (0.15 + (ratio * 0.7));
      }
      if (item.status === 'presigning') return sum + 0.08;
      return sum;
    }, 0);
    return {
      totalCount: batchItems.length,
      settledCount,
      completedCount,
      failedCount,
      isBusy: settledCount < batchItems.length,
      currentItem,
      progressPct: Math.max(0, Math.min(100, Math.round((progressUnits / batchItems.length) * 100))),
    };
  }, [batchItems]);

  const uploading = uploadBatch.isBusy;

  useEffect(() => {
    if (!activeBatchId || !batchItems.length || uploadBatch.isBusy) return;
    if (completedBatchIdsRef.current.has(activeBatchId)) return;
    completedBatchIdsRef.current.add(activeBatchId);

    let active = true;
    (async () => {
      if (useFolders) {
        await loadAssets();
        await loadFolders();
      }
      if (!active) return;
      if (uploadBatch.completedCount > 0) {
        await Promise.resolve(onUploadComplete?.());
      }
    })();

    return () => {
      active = false;
    };
  }, [
    activeBatchId,
    batchItems.length,
    loadAssets,
    loadFolders,
    onUploadComplete,
    uploadBatch.completedCount,
    uploadBatch.isBusy,
    useFolders,
  ]);

  useEffect(() => {
    if (!showEditForm) return;
    setEditUseSelected(Boolean(selectedAssetId));
  }, [selectedAssetId, showEditForm]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const executeGeneration = async ({ mode = 'create', instructions, useBaseImage = false, suppressEmptyAlert = false } = {}) => {
    if (!siteId) {
      alert('Please select a site before generating an image.');
      return null;
    }
    const trimmed = (instructions || '').trim();
    if (!trimmed) {
      if (!suppressEmptyAlert) {
        alert('Please enter a description before generating an image.');
      }
      return null;
    }
    if (mode === 'edit' && useBaseImage && !selectedAssetId) {
      alert('Please select an image to edit or disable "Use selected image".');
      return null;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Please log in again.');
      return null;
    }

    setGenerating(true);
    try {
    const payload = {
      siteId,
      promptText: trimmed,
      mode,
      useSelectedImage: mode === 'edit' ? Boolean(useBaseImage && selectedAssetId) : false,
      assetId: mode === 'edit' && useBaseImage ? selectedAssetId : null,
    };
    if (campaignTitle) {
      payload.campaignTitle = campaignTitle;
    }
    const resp = await fetch('/api/ai/images', {
      method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        const message = body?.error || `Failed to generate image (${resp.status})`;
        throw new Error(message);
      }
      const data = await resp.json().catch(() => ({}));
      const generatedAsset = data?.asset?.id
        ? {
            ...data.asset,
            createdAt: new Date().toISOString(),
            created_at: new Date().toISOString(),
          }
        : null;
      if (generatedAsset?.id && onSelectAsset) {
        onSelectAsset(generatedAsset);
      }
      if (useFolders) {
        await loadAssets();
        await loadFolders();
      }
      if (onUploadComplete) {
        await Promise.resolve(onUploadComplete(generatedAsset));
      }
      if (mode === 'edit') {
        setEditPrompt('');
        setShowEditForm(false);
      }
      return generatedAsset;
    } catch (err) {
      console.error('Failed to generate AI image', err);
      alert(err.message || 'Failed to generate image. Please try again.');
      return null;
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateClick = async () => {
    await executeGeneration({ mode: 'create', instructions: promptText });
  };

  const handleEditSubmit = async () => {
    await executeGeneration({ mode: 'edit', instructions: editPrompt, useBaseImage: editUseSelected });
  };

  useImperativeHandle(ref, () => ({
    generateNewImage: () => executeGeneration({ mode: 'create', instructions: promptText, suppressEmptyAlert: true }),
  }));

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (!siteId) {
      console.warn('Cannot upload without a siteId');
      return;
    }
    const imageFiles = files.filter((file) => file && String(file.type || '').startsWith('image/'));
    if (!imageFiles.length) {
      alert('Please select image files to upload.');
      return;
    }

    const defaultTitle = buildTitleFromFilename(imageFiles[0].name);
    const enteredTitle = window.prompt(
      `Enter a title for this upload${imageFiles.length > 1 ? ' (base title; 1, 2, 3 will be appended)' : ''}`,
      defaultTitle
    );
    if (enteredTitle === null) return;

    const baseTitle = (enteredTitle || '').trim() || defaultTitle;
    const batchId = `photo-browser-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    completedBatchIdsRef.current.delete(batchId);
    const queued = enqueueFiles(siteId, imageFiles, {
      folder: useFolders ? currentFolder : '',
      titleBase: baseTitle,
      batchId,
    });
    if (queued?.length) {
      setActiveBatchId(batchId);
    }
  };

  const currentUploadStatusLabel = useMemo(() => {
    const current = uploadBatch.currentItem;
    if (!current) {
      if (!uploadBatch.totalCount) return '';
      if (uploadBatch.failedCount) {
        return `${uploadBatch.completedCount} uploaded, ${uploadBatch.failedCount} failed.`;
      }
      return `${uploadBatch.completedCount} of ${uploadBatch.totalCount} uploaded.`;
    }
    if (current.status === 'presigning') return `Preparing ${current.filename}…`;
    if (current.status === 'uploading') return `Uploading ${current.filename}…`;
    if (current.status === 'finalizing') return `Processing ${current.filename}…`;
    if (current.status === 'queued') return `Queued: ${current.filename}`;
    return current.filename;
  }, [uploadBatch]);

  return (
    <div className={`bg-gray-900 text-white rounded-lg p-4 flex flex-col ${className || ''}`}>
      <h3 className='text-lg font-semibold mb-3'>Asset Library</h3>
      {useFolders ? (
        <div className="rounded border border-gray-800 bg-gray-900/40 p-3">
          <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Folders</div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              className={`flex items-center gap-2 rounded px-3 py-2 text-sm ${
                currentFolder === '' ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
              }`}
              onClick={() => setCurrentFolder('')}
              type="button"
            >
              🏠 Root
            </button>
            {folderList.map((folder) => (
              <button
                key={folder}
                className={`flex items-center gap-2 rounded px-3 py-2 text-sm ${
                  normalizeFolder(currentFolder) === folder ? 'bg-indigo-600 text-white' : 'text-gray-200 hover:bg-gray-800'
                }`}
                onClick={() => setCurrentFolder(folder)}
                type="button"
              >
                <span>{getFolderEmoji(folder)}</span>
                <span className="truncate max-w-[160px]">{folder}</span>
              </button>
            ))}
            {!folderList.length && <div className="text-xs text-gray-500">No folders yet.</div>}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets..."
              className="flex-1 min-w-[200px] rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500"
            />
          </div>
          {remoteError && <div className="mt-3 text-sm text-rose-300">{remoteError}</div>}
          {remoteLoading && <div className="mt-3 text-sm text-gray-400">Loading assets…</div>}
          {!remoteLoading && (
            <div ref={listRef} className="mt-4 grid grid-cols-2 gap-3 h-[32rem] overflow-y-auto pr-1">
              {photoAssets.length ? (
                photoAssets.map((asset, index) => (
                  <PhotoThumbnail
                    key={asset.id}
                    asset={asset}
                    isSelected={
                      multiSelect
                        ? (Array.isArray(selectedAssetIds) ? selectedAssetIds : []).some((id) => String(id) === String(asset.id))
                        : asset.id === selectedAssetId
                    }
                    selectionIndex={
                      multiSelect
                        ? (Array.isArray(selectedAssetIds) ? selectedAssetIds : []).findIndex((id) => String(id) === String(asset.id)) + 1
                        : null
                    }
                    priority={index < 12}
                    rootRef={listRef}
                    onSelect={(chosen) => {
                      if (multiSelect) {
                        onToggleAsset?.(chosen);
                      } else {
                        onSelectAsset?.(chosen);
                      }
                    }}
                  />
                ))
              ) : (
                <div className='text-sm text-gray-400'>No photos in this folder.</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div ref={listRef} className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 h-96 overflow-y-auto pr-1'>
          {photoAssets.length ? (
            photoAssets.map((asset, index) => (
              <PhotoThumbnail
                key={asset.id}
                asset={asset}
                isSelected={
                  multiSelect
                    ? (Array.isArray(selectedAssetIds) ? selectedAssetIds : []).some((id) => String(id) === String(asset.id))
                    : asset.id === selectedAssetId
                }
                selectionIndex={
                  multiSelect
                    ? (Array.isArray(selectedAssetIds) ? selectedAssetIds : []).findIndex((id) => String(id) === String(asset.id)) + 1
                    : null
                }
                priority={index < 12}
                rootRef={listRef}
                onSelect={(chosen) => {
                  if (multiSelect) {
                    onToggleAsset?.(chosen);
                  } else {
                    onSelectAsset?.(chosen);
                  }
                }}
              />
            ))
          ) : (
            <div className='text-sm text-gray-400'>No photos uploaded yet.</div>
          )}
        </div>
      )}
      {batchItems.length > 0 && (
        <div className='mt-4 rounded border border-gray-800 bg-gray-950/70 p-3'>
          <div className='flex flex-wrap items-center justify-between gap-2 text-sm text-gray-200'>
            <div>
              Upload progress: {uploadBatch.progressPct}% ({uploadBatch.settledCount}/{uploadBatch.totalCount} complete)
            </div>
            <div className='text-xs text-gray-400'>
              {uploadBatch.failedCount
                ? `${uploadBatch.completedCount} done, ${uploadBatch.failedCount} failed`
                : uploadBatch.isBusy
                  ? 'Working…'
                  : 'Finished'}
            </div>
          </div>
          <div className='mt-2 h-2 w-full overflow-hidden rounded bg-gray-800'>
            <div
              className={`h-2 ${uploadBatch.failedCount && !uploadBatch.isBusy ? 'bg-amber-500' : 'bg-indigo-500'}`}
              style={{ width: `${uploadBatch.progressPct}%` }}
            />
          </div>
          <div className='mt-2 text-xs text-gray-300'>{currentUploadStatusLabel}</div>
          <div className='mt-3 space-y-2'>
            {batchItems.map((item) => {
              const pct = item.total ? Math.min(100, Math.round(((item.loaded || 0) / item.total) * 100)) : 0;
              const statusLabel = item.status === 'presigning'
                ? 'Preparing'
                : item.status === 'uploading'
                  ? `Uploading ${pct}%`
                  : item.status === 'finalizing'
                    ? 'Processing'
                    : item.status === 'done'
                      ? 'Done'
                      : item.status === 'error'
                        ? 'Failed'
                        : item.status === 'canceled'
                          ? 'Canceled'
                          : 'Queued';
              return (
                <div key={item.id} className='flex items-center justify-between gap-3 rounded border border-gray-800 bg-gray-900/70 px-3 py-2 text-xs text-gray-300'>
                  <div className='min-w-0 truncate'>{item.filename}</div>
                  <div className={`${item.status === 'error' ? 'text-red-300' : item.status === 'done' ? 'text-emerald-300' : 'text-gray-400'}`}>
                    {statusLabel}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className='flex flex-wrap gap-2 mt-4'>
        {leadingAction}
        <button
          type='button'
          className='px-3 py-2 bg-gray-700 rounded text-sm text-white hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-400 transition'
          onClick={handleGenerateClick}
          disabled={generating || uploading || !siteId || !(promptText || '').trim()}
        >
          {generating ? 'Generating…' : 'Generate New'}
        </button>
        <button
          type='button'
          className={`px-3 py-2 rounded text-sm text-white transition ${
            showEditForm ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-700 hover:bg-gray-600'
          } disabled:bg-gray-800 disabled:text-gray-400`}
          onClick={() => {
            setShowEditForm((prev) => {
              const next = !prev;
              if (!prev) {
                setEditPrompt('');
                setEditUseSelected(Boolean(selectedAssetId));
              }
              return next;
            });
          }}
          disabled={generating || uploading}
        >
          Edit Selected
        </button>
        <button
          type='button'
          className='px-3 py-2 bg-indigo-600 rounded text-sm text-white hover:bg-indigo-500 disabled:bg-indigo-800 transition'
          onClick={handleUploadClick}
          disabled={uploading || generating || !siteId}
        >
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          className='hidden'
          onChange={handleFileChange}
          multiple
        />
      </div>
      {showEditForm && (
        <div className='mt-4 space-y-3 bg-gray-800/70 p-3 rounded-md border border-gray-700'>
          <label className='block text-sm font-medium text-gray-200'>Describe what you want to create</label>
          <textarea
            className='w-full p-2 rounded bg-gray-900 text-white border border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500'
            rows={3}
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            placeholder='e.g. Make the people in the photo look happier'
            disabled={generating}
          />
          <label className='flex items-center gap-2 text-sm text-gray-200'>
            <input
              type='checkbox'
              className='accent-indigo-500'
              checked={editUseSelected && Boolean(selectedAssetId)}
              onChange={(e) => setEditUseSelected(e.target.checked)}
              disabled={!selectedAssetId || generating}
            />
            Use selected image
            {!selectedAssetId && <span className='text-xs text-gray-400'>(Select an image to enable)</span>}
          </label>
          <div className='flex gap-2'>
            <button
              type='button'
              className='px-3 py-2 bg-emerald-600 rounded text-white hover:bg-emerald-500 disabled:bg-emerald-900 transition'
              onClick={handleEditSubmit}
              disabled={generating || !editPrompt.trim()}
            >
              {generating ? 'Submitting…' : 'Generate'}
            </button>
            <button
              type='button'
              className='px-3 py-2 bg-gray-700 rounded text-white hover:bg-gray-600 disabled:bg-gray-800 transition'
              onClick={() => {
                if (generating) return;
                setShowEditForm(false);
                setEditPrompt('');
              }}
              disabled={generating}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default PhotoAssetBrowser;
